<template>
  <div>
    <b-row class="mb-2">
      <b-col cols="12" md="4">
        <b-form-group label="Estado" label-for="affiliate-status" class="mb-0">
          <b-form-select id="affiliate-status" v-model="status" :options="statusOptions" />
        </b-form-group>
      </b-col>
    </b-row>

    <b-alert v-if="errorMessage" show variant="danger">{{ errorMessage }}</b-alert>

    <b-table
      :items="items"
      :fields="fields"
      :busy="loading"
      primary-key="subscription_id"
      responsive
      hover
      show-empty
      empty-text="Sin afiliados."
    >
      <template #table-busy>
        <div class="text-center text-primary my-2">
          <b-spinner class="align-middle"></b-spinner>
        </div>
      </template>

      <template #cell(affiliate)="{ item }">
        <div class="font-weight-bold">{{ ownerName(item) }}</div>
        <b-badge variant="light">{{ kindLabel(item.kind) }}</b-badge>
        <ul v-if="item.members && item.members.length" class="list-unstyled small mt-1 mb-0">
          <li v-for="(member, index) in item.members" :key="index" :class="{ 'text-muted': member.status === 'removed' }">
            {{ member.name }}
            <span v-if="member.relationship"> · {{ member.relationship }}</span>
            <span> · {{ memberTypeLabel(member.member_type) }}</span>
            <span v-if="member.status === 'removed'"> · dado de baja</span>
          </li>
        </ul>
      </template>

      <template #cell(plan)="{ item }">
        {{ item.plan || '—' }}
        <div class="small text-muted">{{ intervalLabel(item.billing_interval) }}</div>
      </template>

      <template #cell(persons)="{ item }">
        <span v-if="item.kind === 'company'">{{ item.persons }} de {{ item.seats }} cupos</span>
        <span v-else>{{ item.persons }}</span>
      </template>

      <template #cell(status)="{ item }">
        <b-badge :variant="badge(item.status).variant">{{ badge(item.status).text }}</b-badge>
        <div v-if="item.status === 'cancel_scheduled' && item.cancel_at" class="small text-muted">Se cancela el {{ item.cancel_at }}</div>
        <div v-if="item.status === 'canceled' && item.canceled_at" class="small text-muted">Cancelado el {{ item.canceled_at }}</div>
        <div v-if="item.guarantee && item.guarantee.in_window" class="small text-warning font-weight-bold">
          {{ guaranteeText(item.guarantee) }}
        </div>
      </template>

      <template #cell(dates)="{ item }">
        <div class="small">Activado: {{ item.activated_at || '—' }}</div>
        <div v-if="item.next_renewal_at" class="small">Renueva: {{ item.next_renewal_at }}</div>
      </template>
    </b-table>

    <p v-if="hasGuarantee" class="small text-muted mb-2">
      En garantía: el cliente todavía puede pedir la devolución y esa comisión puede anularse.
    </p>

    <PaginationTable
      :dataMeta="dataMeta"
      :totalUsers="total"
      :perPage="perPage"
      :currentPage="currentPage"
      @page-changed="load"
    />
  </div>
</template>

<script>
import { BAlert, BBadge, BCol, BFormGroup, BFormSelect, BRow, BSpinner, BTable } from 'bootstrap-vue'
import agentPlanService from '@/core/services/api/agentPlan'
import PaginationTable from '@/components/PaginationTable'
import {
  AFFILIATE_STATUS_COPY,
  AFFILIATE_STATUS_OPTIONS,
  BILLING_INTERVAL_COPY,
  MEMBER_TYPE_COPY,
  PLAN_KIND_COPY,
  statusBadge,
} from '@/core/utils/agentPlanDashboard'
import pagedList from './pagedList'

/** Afiliados del agente agrupados por titular o empresa. Sin fechas: lista todos. */
export default {
  name: 'AgentAffiliatesTable',
  components: { BAlert, BBadge, BCol, BFormGroup, BFormSelect, BRow, BSpinner, BTable, PaginationTable },
  mixins: [pagedList],
  data() {
    return {
      status: null,
      statusOptions: AFFILIATE_STATUS_OPTIONS,
      fields: [
        { key: 'affiliate', label: 'Titular / empresa' },
        { key: 'plan', label: 'Plan' },
        { key: 'persons', label: 'Personas' },
        { key: 'status', label: 'Estado' },
        { key: 'dates', label: 'Fechas' },
      ],
    }
  },
  computed: {
    hasGuarantee() {
      return this.items.some((item) => item.guarantee && item.guarantee.in_window)
    },
  },
  watch: {
    status() {
      this.load(1)
    },
  },
  methods: {
    fetchPage(params) {
      return agentPlanService.getAffiliates(params)
    },
    extraParams() {
      return this.status ? { status: this.status } : {}
    },
    ownerName(item) {
      if (item.kind === 'company') return (item.company && item.company.name) || 'Empresa'
      return item.holder || '—'
    },
    kindLabel(kind) {
      return PLAN_KIND_COPY[kind] || kind || '—'
    },
    intervalLabel(interval) {
      return BILLING_INTERVAL_COPY[interval] || ''
    },
    memberTypeLabel(type) {
      return MEMBER_TYPE_COPY[type] || type || ''
    },
    badge(status) {
      return statusBadge(AFFILIATE_STATUS_COPY, status)
    },
    guaranteeText(guarantee) {
      const days = Number(guarantee.days_left) || 0
      if (days === 0) return 'En garantía, último día'
      return `En garantía, ${days} ${days === 1 ? 'día' : 'días'}`
    },
  },
}
</script>
